import "./RolePermissions.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid2, InputBase, Paper } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  fetchAllPermsissionsService,
  updateRolePermissionService,
} from "../../../services/roleManagementService";
import { notifyError } from "../../../utilities/helper";
import Loader from "../../../components/Loader";
import CustomAPISwitch from "../../../components/CustomSwitch/CustomAPISwitch";
import { SearchSharp } from "@mui/icons-material";

const RolePermissions = () => {
  const [load, setLoad] = useState(false);
  const [permissionData, setPermissionData] = useState(null);
  const [pageName, setPageName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [scrollToKey, setScrollToKey] = useState(null);
  const sectionRefs = useRef({});

  const access_token = localStorage.getItem("access_token");

  const location = useLocation();
  const navigate = useNavigate();

  const roleId = location.state.id;

  useEffect(() => {
    if (roleId) {
      fetchPermissionsData(roleId);
    }
  }, [roleId]);

  // This is for scrolling to that section of the page whose data is updated
  useEffect(() => {
    if (scrollToKey && sectionRefs.current[scrollToKey]) {
      sectionRefs.current[scrollToKey]?.scrollIntoView({ behavior: "smooth" });
      setScrollToKey(null);
    }
  }, [permissionData]); // trigger scroll after new data is rendered

  const filteredPermissionData = useMemo(() => {
    const lowerSearch = (searchTerm || "").toString().toLowerCase();

    if (!lowerSearch) return permissionData;

    const result = {};

    Object.keys(permissionData).forEach((key) => {
      const matchedPermissions = permissionData[key].filter((permission) =>
        permission?.description?.toLowerCase().includes(lowerSearch)
      );

      if (matchedPermissions.length > 0) {
        result[key] = matchedPermissions;
      }
    });

    return result;
  }, [permissionData, searchTerm]);

  const fetchPermissionsData = async (id) => {
    let res = null;
    setLoad(true);
    try {
      res = await fetchAllPermsissionsService(id, access_token);
      if (res?.status === 200) {
        setPermissionData(res?.data?.result?.groupedPermissions);
        setPageName(res?.data?.result?.currentRole?.shortName);
        setLoad(false);
      } else {
        notifyError("Failed to fetch permissions data");
        setLoad(false);
      }
    } catch (err) {
      console.log(err);
      setLoad(false);
      notifyError("Failed to fetch permissions data");
      if (err.status === 401) {
        navigate("/unauthorize");
      }
    }
  };

  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="content-body">
          <p
            className="bread-crumbs-link-container"
            style={{ fontWeight: "600" }}
          >
            <Link className="bread-crumbs-link" to="/">
              Home
            </Link>{" "}
            |{" "}
            <Link className="bread-crumbs-link" to="/role-management">
              Role Management
            </Link>{" "}
            |{" "}
            <Link
              className="bread-crumbs-link"
              to="/role-permissions"
              state={{ id: roleId }}
            >
              Manage Role Permissions
            </Link>
            -{" "}
            <b style={{ color: "gray" }}>
              {localStorage?.getItem("studyName")}
            </b>
          </p>
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={12}>
              <p className="page-heading">{pageName}</p>
            </Grid2>
          </Grid2>
          <Grid2
            container
            alignItems="start"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={12} sx={{ marginTop: "1%", marginBottom: "3%" }}>
              <Paper className="search-bar-conatiner">
                <SearchSharp style={{ width: 20, height: 20 }} />
                <InputBase
                  sx={{ ml: 1, flex: 1, fontSize: 16 }}
                  value={searchTerm}
                  placeholder={`Search Permissions`}
                  inputProps={{ "aria-label": `search permissions` }}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Paper>
            </Grid2>
          </Grid2>
          {filteredPermissionData &&
          Object.keys(filteredPermissionData)?.length > 0 ? (
            <>
              {filteredPermissionData &&
                Object.keys(filteredPermissionData)
                  .sort()
                  .map((key, index) => (
                    <div
                      className="permission-container"
                      key={index}
                      ref={(el) => (sectionRefs.current[key] = el)}
                    >
                      <div className="permission-heading-container">
                        <span className="permission-heading">{key}</span>
                      </div>
                      {filteredPermissionData[key]?.map((permission, index) => (
                        <Grid2
                          key={index}
                          container
                          justifyContent="space-between"
                          alignItems={"center"}
                          sx={{
                            width: "100%",
                            padding: "10px",
                            borderBottom: "1px solid #ccc",
                            ":hover": { backgroundColor: "#f5f5f5" },
                          }}
                        >
                          <Grid2 size={{ lg: 11, md: 11, xl: 11, sm: 11 }}>
                            <span>{permission?.description}</span>
                          </Grid2>
                          <Grid2 size={{ lg: 1, md: 1, xl: 1, sm: 1 }}>
                            <CustomAPISwitch
                              isSwitch={permission?.associatedToUser}
                              api={updateRolePermissionService}
                              data={{
                                roleId: roleId,
                                systemActionId: permission?.id,
                                add: !permission?.associatedToUser,
                              }}
                              onScrollKey={() => setScrollToKey(key)}
                              fetchOriginalData={fetchPermissionsData}
                            />
                          </Grid2>
                        </Grid2>
                      ))}
                    </div>
                  ))}
            </>
          ) : (
            <div className="search-fallback-container">
              <span>No Permissions Found</span>
            </div>
          )}
          <Grid2
            alignItems={"end"}
            justifyContent="end"
            container
            sx={{ marginTop: "2%" }}
          >
            <Grid2 item md={6}>
              <button
                className="create-btn"
                onClick={() => {
                  navigate("/role-management");
                }}
              >
                Back
              </button>
            </Grid2>
          </Grid2>
        </div>
      )}
    </>
  );
};

export default RolePermissions;
